const urlifyCache: Record<string, string> = {}

/**
 * Makes text suited to be used in URLs.
 *
 * Lives in its own module (separate from `utils.ts`) so other shared modules
 * — like `judiciaryMonitoring.ts`, which `utils.ts` itself imports from —
 * can use it without creating an import cycle.
 */
export function urlify(text: string, { lowerCase = true } = {}): string {
  if (!text) return ''

  if (urlifyCache[text]) {
    return urlifyCache[text] ?? ''
  }

  if (lowerCase) text = text.toLowerCase()

  const isEu = /^eu\.\d+/i.test(text)

  urlifyCache[text] = encodeURIComponent(
    text
      // "u00A0" is a non-breaking space, which is used sometimes, so handle it as well (%C2%A0). E.g. "/bundesrecht/verordnungen/eregv%C2%A02022"
      .replace(/ |\u00A0|\/|:/g, '-')
      .replace(isEu ? /,|\(|\)/g : /\.|,|'|\(|\)/g, '')
      .replace(/ä/g, 'a') // Replace with simple letter "a" instead of "ae", because that's what Algolia understands
      .replace(/ö/g, 'o') // Umlaut-removal inspired by https://stackoverflow.com/a/40015184/8464260
      .replace(/ü/g, 'u')
      .replace(/ß/g, 'ss')
      .replaceAll('§', 'para'),
  )

  return urlifyCache[text] ?? ''
}

const determUrlifyCache: Record<string, string> = {}

/**
 * Custom version of the `urlify()` function which only does deterministic changes, which can be reversed to the original string with `resolveUrlify()`
 */
export function deterministicUrlify(text: string): string {
  if (determUrlifyCache[text]) {
    return determUrlifyCache[text]
  }

  determUrlifyCache[text] = encodeURIComponent(
    text
      .toLowerCase()
      .replace(/ /g, '-')
      .replace(/\./g, '')
      .replaceAll('§', 'para'),
  )

  return determUrlifyCache[text]
}

/**
 * Returns a urlified anchor that is unique within a single page.
 * Duplicates get a `-2`, `-3`, … suffix.
 * Both the ToC (`TocServer.tsx`) and the body (`Content.tsx`) walk
 * the tree in the same depth-first order, so passing a fresh `seen`
 * map to each produces matching hrefs ↔ ids.
 */
export function uniqueAnchor(raw: string, seen: Map<string, number>): string {
  const base = urlify(raw)
  const count = (seen.get(base) ?? 0) + 1
  seen.set(base, count)
  return count === 1 ? base : `${base}-${count}`
}

/**
 * Reverses the urlified form of a `deterministicUrlify()` string into its original form
 */
export function resolveUrlify(text: string): string {
  return decodeURIComponent(
    text
      .replaceAll('-', ' ')
      .replace('para', '§')
      .replace('anl', 'Anl.')
      .replace('art', 'Art.'),
  )
}
